import { useCallback, useEffect, useRef } from 'react';

import { batch } from 'editor/src/store/batchedSubscribeEnhancer';
import updateImageIdByUuidOperation from 'editor/src/store/design/operation/updateImageIdByUuidOperation';
import updateMediaElementOperation, {
  MediaUpdateActionName,
} from 'editor/src/store/design/operation/updateMediaElementOperation';
import getStructureIndexByElementUuid from 'editor/src/store/design/selector/getStructureIndexByElementUuid';
import { DesignData, MediaImage, MediaText } from 'editor/src/store/design/types';
import { useDispatch, useSelector, useStore } from 'editor/src/store/hooks';

import {
  buildQrContentFromConfig,
  buildQrPayload,
  DEFAULT_QR_SIZE,
  DEFAULT_QR_STYLE,
  generateQrImage,
  isContentComplete,
  qrBlobToFile,
} from 'editor/src/lib/qr';
import getCenteredImagePosition from 'editor/src/util/2d/getCenteredImagePosition';

import type { Uploader } from 'editor/src/component/Uploader/useUploader';

// Give the buyer a beat to finish typing before regenerating + uploading a QR.
const REGENERATE_DEBOUNCE_MS = 600;

interface QrElementState {
  uuid: number;
  payload: string; // current vCard payload resolved from the linked fields
  encoded: string | undefined; // payload baked into the element's current image
  hasValues: boolean;
}

function collectQrElements(designData: DesignData | undefined): QrElementState[] {
  if (!designData) {
    return [];
  }
  const texts: Record<number, string> = {};
  designData.spreads.forEach((spread) =>
    spread.pages.forEach((page) =>
      page.groups.media?.forEach((el) => {
        if (el.type === 'text' && !el.personalizationLocked) {
          texts[el.uuid] = (el as MediaText).extra?.text ?? '';
        }
      }),
    ),
  );

  const items: QrElementState[] = [];
  designData.spreads.forEach((spread) =>
    spread.pages.forEach((page) =>
      page.groups.media?.forEach((el) => {
        if (el.type === 'image' && el.qrCode?.fields.length) {
          const content = buildQrContentFromConfig(el.qrCode.type, el.qrCode.fields, (id) => texts[id]);
          items.push({
            uuid: el.uuid,
            payload: buildQrPayload(content),
            encoded: el.qrCode.encoded,
            hasValues: isContentComplete(content),
          });
        }
      }),
    ),
  );
  return items;
}

/**
 * QR code personalization (GCIN-3610): watches every image element carrying a
 * qrCode config and the personalizable text fields it links to. Whenever the
 * resolved payload (for the seller-chosen content type — URL, contact card, email,
 * phone, SMS, WiFi or text) changes, the QR image is regenerated with the fixed
 * default style, pushed through the regular upload pipeline and placed into the
 * element (contain-fit — a cropped QR is unscannable). The buyer never configures
 * anything: the QR simply follows their personalization input.
 */
function useLiveQrCodes(uploader: Uploader) {
  const store = useStore();
  const dispatch = useDispatch();

  const timers = useRef<Record<number, ReturnType<typeof setTimeout>>>({});
  const generation = useRef<Record<number, number>>({});
  // upload file id -> the element + payload the upload was generated for
  const pendingUploads = useRef<Record<string, { uuid: number; payload: string }>>({});

  // A single string snapshot keeps the selector cheap to compare and re-runs the
  // effect only when a QR-relevant part of the design actually changed.
  const snapshot = useSelector((state) => JSON.stringify(collectQrElements(state.design.designData)));

  const regenerate = useCallback(async (uuid: number) => {
    // Re-resolve at fire time — the buyer may have kept typing since the schedule.
    const state = store.getState();
    const items = collectQrElements(state.design.designData);
    const item = items.find((entry) => entry.uuid === uuid);
    if (!item || !item.hasValues || item.payload === item.encoded) {
      return;
    }

    const gen = (generation.current[uuid] ?? 0) + 1;
    generation.current[uuid] = gen;

    const texts: Record<number, string> = {};
    state.design.designData?.spreads.forEach((spread) =>
      spread.pages.forEach((page) =>
        page.groups.media?.forEach((el) => {
          if (el.type === 'text') {
            texts[el.uuid] = (el as MediaText).extra?.text ?? '';
          }
        }),
      ),
    );
    const structureIndex = getStructureIndexByElementUuid(state, uuid);
    const element = structureIndex
      ? (state.design.designData?.spreads[structureIndex.spreadIndex]?.pages[structureIndex.pageIndex]?.groups.media?.[
          structureIndex.elementIndex
        ] as MediaImage | undefined)
      : undefined;
    if (!element?.qrCode) {
      return;
    }

    const content = buildQrContentFromConfig(element.qrCode.type, element.qrCode.fields, (id) => texts[id]);
    let blob: Blob;
    try {
      ({ blob } = await generateQrImage({ content, style: DEFAULT_QR_STYLE, size: DEFAULT_QR_SIZE }));
    } catch (error) {
      // Payload too dense for the QR, or canvas unavailable. Keep the previous image;
      // the next field change retries, and getPersonalizationErrors keeps the order
      // blocked while the QR is out of date.
      // eslint-disable-next-line no-console
      console.warn('[qr] QR generation failed', error);
      return;
    }
    if (generation.current[uuid] !== gen) {
      return; // a newer regeneration superseded this one
    }

    const file = qrBlobToFile(blob, `qr-${uuid}`);
    const uploads = uploader.upload([file]);
    const uploadId = uploads[0]?.id;
    if (uploadId) {
      pendingUploads.current[uploadId] = { uuid, payload: item.payload };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const items: QrElementState[] = JSON.parse(snapshot);
    items.forEach((item) => {
      if (!item.hasValues || item.payload === item.encoded) {
        return;
      }
      clearTimeout(timers.current[item.uuid]);
      timers.current[item.uuid] = setTimeout(() => {
        void regenerate(item.uuid);
      }, REGENERATE_DEBOUNCE_MS);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshot]);

  useEffect(
    () => () => {
      Object.values(timers.current).forEach(clearTimeout);
    },
    [],
  );

  /**
   * Called from the shared onUploadSuccess once the asset is in the gallery.
   * Returns true when the upload was one of ours (so the caller must not route it
   * into the crop tool).
   */
  const onQrUploadSuccess = useCallback((uploadFileId: string, assetId: string): boolean => {
    const pendingUpload = pendingUploads.current[uploadFileId];
    if (!pendingUpload) {
      return false;
    }
    delete pendingUploads.current[uploadFileId];

    const state = store.getState();
    const structureIndex = getStructureIndexByElementUuid(state, pendingUpload.uuid);
    const element = structureIndex
      ? (state.design.designData?.spreads[structureIndex.spreadIndex]?.pages[structureIndex.pageIndex]?.groups.media?.[
          structureIndex.elementIndex
        ] as MediaImage | undefined)
      : undefined;
    if (!structureIndex || !element?.qrCode) {
      return true;
    }

    const imagePos = getCenteredImagePosition(element, { width: DEFAULT_QR_SIZE, height: DEFAULT_QR_SIZE });
    batch(() => {
      // No undo step: auto-placement is not a user action, and an undo snapshot here
      // would both pollute the stack and be immediately re-done by this hook.
      dispatch(updateImageIdByUuidOperation(pendingUpload.uuid, assetId, false));
      dispatch(
        updateMediaElementOperation(
          structureIndex,
          { ...imagePos, pr: 0, qrCode: { ...element.qrCode!, encoded: pendingUpload.payload } },
          MediaUpdateActionName.CROPPED,
          true,
        ),
      );
    });
    return true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { onQrUploadSuccess };
}

export default useLiveQrCodes;
